// AAL-86 MyServices Copilot: Client assignments and sales channels of the Service Designer (ticket 21).
//
// `salesChannelAccess` mirrors the edit rules of the page's sales channel section
// (SalesChannelSelectorContainer / ClientAssignments behind FEATURE_NEW_SALES_CHANNEL_COMPONENT) for
// the current user, so the Form Contract marks the two fields writable only where the page would
// let this user change them. See README.md in this folder, "Client assignments and sales channels".
// The builders produce the entries the page itself creates when a Client or a channel is ticked.

export type SalesChannelAccessInput = {
	/** Common.userIsOperator: false for a Service Provider and for an Operator impersonating one */
	userIsOperator: boolean
	/** trust level of the Service's provider (3 may always pick channels) */
	spTrustLevel?: number
	/** selected Client's `marketplaceSettings` (spSalesChannelSelectionEnabled) */
	marketplaceSettings?: { id: string, enabled?: boolean }[]
	/** Common.userMayEditSharedProductContent (MYS-7453 edit scope; true for Service Providers) */
	mayEditSharedContent: boolean
	productIsExternal: boolean
	/** client feature `sync-external-product-channels` */
	syncExternalChannels: boolean
	/** ticket-level channeling is on: the page shows only the per-ticket assignments */
	ticketLevelOnly: boolean
	/** the section component is mounted and has loaded the Clients it offers */
	selectorLoaded: boolean
	/** the Service has a client assignment for the selected Client */
	hasCurrentAssignment: boolean
}

export type SalesChannelAccess = { clientAssignments: boolean, salesChannels: boolean }

export function salesChannelAccess(input: SalesChannelAccessInput): SalesChannelAccess {
	// the section's lock: <Disclosure :lock="salesChannelSelectionLocked || locked">
	const channelSelectionLocked = input.productIsExternal && !input.syncExternalChannels
	const open = input.selectorLoaded && input.mayEditSharedContent && !channelSelectionLocked
	return {
		// clientAssignmentLocked = productIsExternal
		clientAssignments: open && !input.productIsExternal,
		// channels of the selected Client only (isCurrentClientEditing), and only with enableSelection
		salesChannels: open && enableSelection(input) && input.hasCurrentAssignment && !input.ticketLevelOnly,
	}
}

// The page's `enableSelection`: trust level 3 or Common.spEnabledSetting, which is always true for an
// Operator (and false-unless-enabled for a Service Provider, an impersonating Operator included).
function enableSelection(input: SalesChannelAccessInput): boolean {
	if (input.spTrustLevel === 3 || input.userIsOperator) return true
	return input.marketplaceSettings?.find(setting => setting.id === 'spSalesChannelSelectionEnabled')?.enabled === true
}

/**
 * The apply step's `lockedItemKey` for this section: a mandatory sales channel of the current Client (keyed by
 * its channel id) and, while the section does not let the home Client go, the home client assignment (keyed by
 * its Client id). Items the Copilot or the page just built are judged the same way as saved ones.
 */
export function lockedItemKey(locks: { homeLocked: boolean, mandatoryChannelIds: string[] }) {
	return (field: { key: string }, item: any): string | undefined => {
		if (field.key === 'salesChannels') {
			const id = item?.fields?.salesChannel?.de?.sys?.id
			return locks.mandatoryChannelIds.includes(id) ? id : undefined
		}
		if (field.key === 'clientAssignments' && locks.homeLocked && item?.fields?.isHomebase?.de === true) return item.fields.client?.de?.sys?.id
		return undefined
	}
}

const uniqueId = (prefix: string) => `${prefix}-${Date.now()}-${Math.floor(Math.random() * 99999)}`

/** SalesChannelSelectorContainer.sendData's new sales channel assignment */
export function newSalesChannelAssignment(channel: any, id = uniqueId('MUI-SCA')) {
	return {
		sys: { id },
		fields: {
			title: { de: channel.fields?.title?.de?.length ? channel.fields.title.de : channel.sys.id },
			salesChannel: { de: { sys: { id: channel.sys.id, type: 'Link', linkType: 'Entry' } } },
			resellerCategories: { de: [] },
		},
		addl: { isResellerChannel: channel.fields?.isResellerChannel?.de === true },
	}
}

/**
 * ClientAssignments.getNewClientAssignment, with the Client's mandatory sales channels the page ticks
 * on its own. `client` is the full Client entry the selector offers (its sales channels included).
 */
export function newClientAssignment(client: any, options: { isHomebase: boolean, id?: string, scaId?: () => string }) {
	const mandatory = (client.fields?.salesChannels?.de ?? []).filter((channel: any) => channel?.fields?.mandatory?.de === true)
	return {
		sys: { id: options.id ?? `${uniqueId('MUI-CA')}-${client.sys.id}` },
		fields: {
			title: { de: 'CA' },
			client: { de: client },
			regions: { de: [] },
			salesChannelAssignments: { de: mandatory.map((channel: any) => newSalesChannelAssignment(channel, options.scaId?.())) },
			taxClass: { de: null },
			status: { de: 'pending' },
			isHomebase: { de: options.isHomebase },
		},
		addl: { isNew: true },
	}
}
